import { FocusEvent, useRef } from "react";
import { Autocomplete, TextField } from "@mui/material";
import {
  ParsedAddress,
  parseAddressComponents,
} from "@/shared/utils/parse-address-components";

interface OrganizationAddressAutocompleteProps {
  value: string;
  onAddressChange: (value: string, address: ParsedAddress | null) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  error?: boolean;
  helperText?: string | false;
  placePredictions: google.maps.places.AutocompletePrediction[];
  getPlacePredictions: (params: { input: string }) => void;
  getAddressComponents: (
    placeId: string,
  ) => Promise<google.maps.GeocoderAddressComponent[] | null>;
}

const OrganizationAddressAutocomplete = ({
  value,
  onAddressChange,
  onBlur,
  error,
  helperText,
  placePredictions,
  getPlacePredictions,
  getAddressComponents,
}: OrganizationAddressAutocompleteProps) => {
  const latestRequestRef = useRef(0);

  const handlePredictionSelected = async (
    prediction: google.maps.places.AutocompletePrediction,
  ) => {
    const requestId = ++latestRequestRef.current;

    const components = await getAddressComponents(prediction.place_id);
    if (requestId !== latestRequestRef.current) {
      return;
    }

    onAddressChange(
      prediction.description,
      components ? parseAddressComponents(components) : null,
    );
  };

  return (
    <Autocomplete
      freeSolo
      filterOptions={(x) => x}
      inputValue={value ?? ""}
      options={placePredictions.map((prediction) => ({
        label: prediction.description,
        value: prediction,
      }))}
      onInputChange={(_evt, newValue, reason) => {
        if (reason === "input" || reason === "clear") {
          latestRequestRef.current += 1;
          onAddressChange(newValue, null);
          getPlacePredictions({ input: newValue.trim() });
        }
      }}
      onChange={(_evt, selected) => {
        if (selected == null) {
          latestRequestRef.current += 1;
          onAddressChange("", null);
        } else if (typeof selected === "string") {
          latestRequestRef.current += 1;
          onAddressChange(selected, null);
        } else {
          handlePredictionSelected(selected.value);
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          id="address"
          name="address"
          placeholder="Enter Address"
          data-testid="address"
          error={error}
          helperText={helperText}
          onBlur={onBlur}
          fullWidth
        />
      )}
    />
  );
};

export default OrganizationAddressAutocomplete;
